import React, { useState, useEffect } from 'react';
import { 
  MapPin, Package, Search, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
  Plus, Save, X, Check, ShoppingCart, Edit3, AlertTriangle, DollarSign,
  Star, ExternalLink, Calendar, Filter, Eye, Trash2, CheckCircle
} from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';
import API_URL from '../config/ApiUrl';

export const DispensaryProductManagement = () => {
  // Dispensary browsing state
  const [dispensaries, setDispensaries] = useState([]);
  const [dispensarySearch, setDispensarySearch] = useState('');
  const [dispensaryPage, setDispensaryPage] = useState(1);
  const [dispensaryTotalPages, setDispensaryTotalPages] = useState(1);
  const [dispensaryLoading, setDispensaryLoading] = useState(false);
  const [selectedDispensary, setSelectedDispensary] = useState(null);

  const { colors } = useTheme(); 
  // Product browsing state
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productLoading, setProductLoading] = useState(false);
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Dispensary product creation state
  const [showDispensaryProductModal, setShowDispensaryProductModal] = useState(false);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [dispensaryProductData, setDispensaryProductData] = useState({});
  const [saving, setSaving] = useState(false);

  const [syncingRatings, setSyncingRatings] = useState(false);
  const [syncResults, setSyncResults] = useState(null);


  // New product creation state
  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [newProductsToAdd, setNewProductsToAdd] = useState(0);
  const [currentNewProductIndex, setCurrentNewProductIndex] = useState(0);
  const [newProductData, setNewProductData] = useState({
    name: '',
    brand: '',
    type: 'flower',
    attributes: {}
  });

  // Load dispensaries on component mount
  useEffect(() => {
    loadDispensaries();
  }, [dispensaryPage, dispensarySearch]);

  // Load products when dispensary is selected
  useEffect(() => {
    if (selectedDispensary) {
      loadProducts();
    }
  }, [selectedDispensary, productPage, productSearch, productTypeFilter]);

  const loadDispensaries = async () => {
    setDispensaryLoading(true);
    try {
      const params = new URLSearchParams({
        page: dispensaryPage.toString(),
        per_page: '25',
        search: dispensarySearch
      });

      const response = await fetch(`${API_URL}/api/admin/dispensaries?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDispensaries(data.dispensaries || []);
        setDispensaryTotalPages(data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Failed to load dispensaries:', error);
    } finally {
      setDispensaryLoading(false);
    }
  };

  const handleSyncRatings = async () => {
    setSyncingRatings(true);
    setSyncResults(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/sync-platform-ratings`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSyncResults(data);
        
        setTimeout(() => {
          setSyncResults(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to sync ratings:', error);
    } finally {
      setSyncingRatings(false);
    }
  };

  const loadProducts = async () => {
    setProductLoading(true);
    try {
      const params = new URLSearchParams({
        page: productPage.toString(),
        per_page: '50',
        search: productSearch,
        type: productTypeFilter
      });

      const response = await fetch(`${API_URL}/api/admin/products?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
        setProductTotalPages(data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setProductLoading(false);
    }
  };

  const handleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.find(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleContinueWithSelected = () => {
    if (selectedProducts.length === 0) return;
    
    // Initialize dispensary product data for all selected products
    const initialData = {};
    selectedProducts.forEach(product => {
      initialData[product.id] = {
        external_platform: 'Jane',
        external_product_id: '',
        variant: product.attributes?.size || '',
        price_cents: '',
        in_stock: true,
        url: '',
        platform_rating: '',
        name_snapshot: product.name,
        brand_snapshot: product.brand,
        type_snapshot: product.type,
        form_snapshot: product.type,
        raw_json: ''
      };
    });
    
    setDispensaryProductData(initialData);
    setCurrentProductIndex(0);
    setShowDispensaryProductModal(true);
  };

  const handleDispensaryProductDataChange = (field, value) => {
    const currentProduct = selectedProducts[currentProductIndex];
    setDispensaryProductData(prev => ({
      ...prev,
      [currentProduct.id]: {
        ...prev[currentProduct.id],
        [field]: value
      }
    }));
  };

  const handleNextProduct = () => {
    if (currentProductIndex < selectedProducts.length - 1) {
      setCurrentProductIndex(currentProductIndex + 1);
    }
  };

  const handlePrevProduct = () => {
    if (currentProductIndex > 0) {
      setCurrentProductIndex(currentProductIndex - 1);
    }
  };

  const handleSaveDispensaryProducts = async () => {
    setSaving(true);
    try {
      const dispensaryProducts = selectedProducts.map(product => ({
        dispensary_id: selectedDispensary.id,
        product_id: product.id,
        ...dispensaryProductData[product.id],
        price_cents: dispensaryProductData[product.id].price_cents ? 
          parseInt(dispensaryProductData[product.id].price_cents * 100) : null,
        platform_rating: dispensaryProductData[product.id].platform_rating ? 
          parseFloat(dispensaryProductData[product.id].platform_rating) : null
      }));

      const response = await fetch(`${API_URL}/api/admin/dispensary-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ dispensary_products: dispensaryProducts })
      });

      if (response.ok) {
        setShowDispensaryProductModal(false);
        setSelectedProducts([]);
        setDispensaryProductData({});
        setCurrentProductIndex(0);
        // Show success message or refresh data
      }
    } catch (error) {
      console.error('Failed to save dispensary products:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewProducts = () => {
    const count = prompt('How many new products would you like to add?');
    const numProducts = parseInt(count);
    
    if (numProducts && numProducts > 0) {
      setNewProductsToAdd(numProducts);
      setCurrentNewProductIndex(0);
      setNewProductData({
        name: '',
        brand: '',
        type: 'flower',
        attributes: {
          THC: '',
          CBD: '',
          strain_type: 'Hybrid',
          size: '3.5g'
        }
      });
      setShowNewProductModal(true);
    }
  };

  const handleNewProductDataChange = (field, value) => {
    if (field === 'type') {
      let defaultAttributes = {};
      
      if (value === 'cart') {
        defaultAttributes = {
          THC: '',
          CBD: '',
          flavor: '',
          strain_type: 'Hybrid',
          category: 'Distillate',
          size: '1g'
        };
      } else if (value === 'edible') {
        defaultAttributes = {
          THC: '',
          CBD: '',
          flavor: '',
          strain_type: 'Hybrid',
          category: 'Gummies',
          dosage_per_unit: '',
          size: ''
        };
      } else {
        defaultAttributes = {
          THC: '',
          CBD: '',
          strain_type: 'Hybrid',
          size: '3.5g'
        };
      }
      
      setNewProductData(prev => ({
        ...prev,
        type: value,
        attributes: defaultAttributes
      }));
    } else if (field.startsWith('attributes.')) {
      const attrKey = field.replace('attributes.', '');
      setNewProductData(prev => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [attrKey]: value
        }
      }));
    } else {
      setNewProductData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSaveNewProduct = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/direct-upload-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newProductData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Add the new product to selected products
        setSelectedProducts(prev => [...prev, result.product]);
        
        // Initialize dispensary product data for the new product
        setDispensaryProductData(prev => ({
          ...prev,
          [result.product.id]: {
            external_platform: 'Jane',
            external_product_id: '',
            variant: result.product.attributes?.size || '',
            price_cents: '',
            in_stock: true,
            url: '',
            platform_rating: '',
            name_snapshot: result.product.name,
            brand_snapshot: result.product.brand,
            type_snapshot: result.product.type,
            form_snapshot: result.product.type,
            raw_json: ''
          }
        }));

        if (currentNewProductIndex < newProductsToAdd - 1) {
          setCurrentNewProductIndex(currentNewProductIndex + 1);
          setNewProductData({
            name: '',
            brand: '',
            type: 'flower',
            attributes: {
              THC: '',
              CBD: '',
              strain_type: 'Hybrid',
              size: '3.5g'
            }
          });
        } else {
          setShowNewProductModal(false);
          setNewProductsToAdd(0);
          setCurrentNewProductIndex(0);
        }
      }
    } catch (error) {
      console.error('Failed to save new product:', error);
    } finally {
      setSaving(false);
    }
  };

  // If no dispensary is selected, show dispensary browser
  if (!selectedDispensary) {
    return (
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '700',
            color: '#e5e5e5'
          }}>
            Dispensary Product Management
          </h2>
        </div>
        <button
            onClick={handleSyncRatings}
            disabled={syncingRatings}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: syncingRatings ? '#555' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: syncingRatings ? 'not-allowed' : 'pointer'
            }}
          >
            {syncingRatings ? 'Syncing...' : 'Sync Platform Ratings'}
          </button>

        {/* Search */}
        <div style={{
          background: colors.bgTertiary,
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid #333',
          marginBottom: '24px'
        }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <Search size={20} style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888'
            }} />
            <input
              type="text"
              placeholder="Search dispensaries..."
              value={dispensarySearch}
              onChange={(e) => setDispensarySearch(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '48px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '14px',
                background: '#2a2a2a',
                color: '#e5e5e5',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Dispensaries Grid */}
        <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
          {dispensaries.map(dispensary => (
            <div
              key={dispensary.id}
              style={{
                background: colors.bgTertiary,
                borderRadius: '16px',
                padding: '20px',
                border: '1px solid #333',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedDispensary(dispensary)}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#8b5cf6';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MapPin size={24} style={{ color: 'white' }} />
                </div>
                
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: '0 0 8px 0',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    {dispensary.name}
                  </h3>
                  
                  <div style={{ color: '#888', fontSize: '14px', marginBottom: '4px' }}>
                    {dispensary.address}, {dispensary.city}, {dispensary.state}
                  </div>
                  
                  {dispensary.website && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <ExternalLink size={12} style={{ color: '#888' }} />
                      <span style={{ color: '#888', fontSize: '12px' }}>
                        {dispensary.website}
                      </span>
                    </div>
                  )}
                </div>
                
                <ChevronRight size={20} style={{ color: '#888' }} />
              </div>
            </div>
          ))}

          {dispensaryLoading && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#888'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #333',
                borderTop: '3px solid #8b5cf6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              Loading dispensaries...
            </div>
          )}

          {dispensaries.length === 0 && !dispensaryLoading && (
            <div style={{
              textAlign: 'center',
              padding: '64px 24px',
              background: colors.bgTertiary,
              borderRadius: '20px',
              border: '1px solid #333'
            }}>
              <MapPin size={48} style={{ color: '#666', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: '20px', fontWeight: '600' }}>
                No dispensaries found
              </h3>
              <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
                {dispensarySearch ? 'Try adjusting your search criteria' : 'No dispensaries available'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {dispensaryTotalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            marginTop: '24px'
          }}>
            <button
              onClick={() => setDispensaryPage(1)}
              disabled={dispensaryPage === 1}
              style={{
                padding: '8px',
                background: dispensaryPage === 1 ? '#222' : '#333',
                color: dispensaryPage === 1 ? '#555' : '#ccc',
                border: 'none',
                borderRadius: '6px',
                cursor: dispensaryPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            
            <button
              onClick={() => setDispensaryPage(dispensaryPage - 1)}
              disabled={dispensaryPage === 1}
              style={{
                padding: '8px',
                background: dispensaryPage === 1 ? '#222' : '#333',
                color: dispensaryPage === 1 ? '#555' : '#ccc',
                border: 'none',
                borderRadius: '6px',
                cursor: dispensaryPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              <ChevronLeft size={16} />
            </button>
            
            <span style={{ color: '#ccc', margin: '0 16px' }}>
              Page {dispensaryPage} of {dispensaryTotalPages}
            </span>
            
            <button
              onClick={() => setDispensaryPage(dispensaryPage + 1)}
              disabled={dispensaryPage === dispensaryTotalPages}
              style={{
                padding: '8px',
                background: dispensaryPage === dispensaryTotalPages ? '#222' : '#333',
                color: dispensaryPage === dispensaryTotalPages ? '#555' : '#ccc',
                border: 'none',
                borderRadius: '6px',
                cursor: dispensaryPage === dispensaryTotalPages ? 'not-allowed' : 'pointer'
              }}
            >
              <ChevronRight size={16} />
            </button>
            
            <button
              onClick={() => setDispensaryPage(dispensaryTotalPages)}
              disabled={dispensaryPage === dispensaryTotalPages}
              style={{
                padding: '8px',
                background: dispensaryPage === dispensaryTotalPages ? '#222' : '#333',
                color: dispensaryPage === dispensaryTotalPages ? '#555' : '#ccc',
                border: 'none',
                borderRadius: '6px',
                cursor: dispensaryPage === dispensaryTotalPages ? 'not-allowed' : 'pointer'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Product selection interface
  return (
    <div>
      {/* Header with back button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <button
          onClick={() => {
            setSelectedDispensary(null);
            setSelectedProducts([]);
            setProductPage(1);
            setProductSearch('');
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: '#333',
            color: '#ccc',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          ← Back to Dispensaries
        </button>
        
        <div>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '700',
            color: '#e5e5e5'
          }}>
            {selectedDispensary.name}
          </h2>
          <p style={{
            margin: '4px 0 0 0',
            color: '#888',
            fontSize: '14px'
          }}>
            Select products to add to this dispensary
          </p>
        </div>
      </div>

      {/* Selected products summary */}
      {selectedProducts.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                {selectedProducts.length} Products Selected
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                Ready to configure dispensary details
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleAddNewProducts}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <Plus size={16} />
                Add New Products
              </button>
              
              <button
                onClick={handleContinueWithSelected}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  background: 'white',
                  color: '#059669',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <ShoppingCart size={16} />
                Continue with Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and filters */}
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '16px',
        padding: '20px',
        border: '1px solid #333',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
            <Search size={20} style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888'
            }} />
            <input
              type="text"
              placeholder="Search products..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '48px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '14px',
                background: '#2a2a2a',
                color: '#e5e5e5',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <select
            value={productTypeFilter}
            onChange={(e) => setProductTypeFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '2px solid #333',
              borderRadius: '12px',
              fontSize: '14px',
              background: '#2a2a2a',
              color: '#e5e5e5',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Types</option>
            <option value="flower">Flower</option>
            <option value="cart">Cartridges</option>
            <option value="edible">Edibles</option>
            <option value="pre-roll">Pre-Rolls</option>
            <option value="concentrate">Concentrates</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Products spreadsheet */}
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '16px',
        border: '1px solid #333',
        overflow: 'hidden',
        marginBottom: '24px'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '50px 1fr 120px 120px 100px 120px 120px 80px',
          gap: '16px',
          padding: '16px',
          background: '#2a2a2a',
          borderBottom: `1px solid ${colors.border}`,
          fontSize: '14px',
          fontWeight: '600',
          color: '#e5e5e5'
        }}>
          <div>Select</div>
          <div>Product</div>
          <div>Brand</div>
          <div>Type</div>
          <div>THC/CBD</div>
          <div>Strain</div>
          <div>Size</div>
          <div>Rating</div>
        </div>

        {/* Table Body */}
        <div style={{ maxHeight: '600px', overflow: 'auto' }}>
          {products.map(product => {
            const isSelected = selectedProducts.find(p => p.id === product.id);
            
            return (
              <div
                key={product.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '50px 1fr 120px 120px 100px 120px 120px 80px',
                  gap: '16px',
                  padding: '16px',
                  borderBottom: `1px solid ${colors.border}`,
                  background: isSelected ? '#8b5cf620' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => handleProductSelection(product)}
                onMouseOver={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = '#2a2a2a20';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: '2px solid #333',
                    background: isSelected ? '#8b5cf6' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {isSelected && <Check size={12} style={{ color: 'white' }} />}
                  </div>
                </div>
                
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#e5e5e5',
                    marginBottom: '2px'
                  }}>
                    {product.name}
                  </div>
                  {product.averageRating > 0 && (
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      {product.reviewCount} reviews
                    </div>
                  )}
                </div>
                
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  {product.brand}
                </div>
                
                <div>
                  <span style={{
                    padding: '4px 8px',
                    background: product.type === 'flower' ? '#10b98120' : 
                                product.type === 'cart' ? '#8b5cf620' :
                                product.type === 'edible' ? '#f59e0b20' : '#6b728020',
                    color: product.type === 'flower' ? '#10b981' : 
                            product.type === 'cart' ? '#8b5cf6' :
                            product.type === 'edible' ? '#f59e0b' : '#6b7280',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}>
                    {product.type}
                  </span>
                </div>
                
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  {product.attributes?.THC && (
                    <div>THC: {product.attributes.THC}</div>
                  )}
                  {product.attributes?.CBD && (
                    <div>CBD: {product.attributes.CBD}</div>
                  )}
                </div>
                
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  {product.attributes?.strain_type || '-'}
                </div>
                
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  {product.attributes?.size || '-'}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {product.averageRating > 0 ? (
                    <>
                      <Star size={12} style={{ color: '#f59e0b' }} />
                      <span style={{ fontSize: '14px', color: '#ccc' }}>
                        {product.averageRating}
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: '14px', color: '#666' }}>-</span>
                  )}
                </div>
              </div>
            );
          })}

          {productLoading && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#888'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #333',
                borderTop: '3px solid #8b5cf6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              Loading products...
            </div>
          )}

          {products.length === 0 && !productLoading && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#888'
            }}>
              <Package size={48} style={{ color: '#666', marginBottom: '16px' }} />
              <div style={{ fontSize: '16px', color: '#e5e5e5', marginBottom: '8px' }}>
                No products found
              </div>
              <div style={{ fontSize: '14px' }}>
                {productSearch ? 'Try adjusting your search criteria' : 'No products available'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product pagination */}
      {productTotalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '24px'
        }}>
          <button
            onClick={() => setProductPage(1)}
            disabled={productPage === 1}
            style={{
              padding: '8px',
              background: productPage === 1 ? '#222' : '#333',
              color: productPage === 1 ? '#555' : '#ccc',
              border: 'none',
              borderRadius: '6px',
              cursor: productPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            <ChevronsLeft size={16} />
          </button>
          
          <button
            onClick={() => setProductPage(productPage - 1)}
            disabled={productPage === 1}
            style={{
              padding: '8px',
              background: productPage === 1 ? '#222' : '#333',
              color: productPage === 1 ? '#555' : '#ccc',
              border: 'none',
              borderRadius: '6px',
              cursor: productPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            <ChevronLeft size={16} />
          </button>
          
          <span style={{ color: '#ccc', margin: '0 16px' }}>
            Page {productPage} of {productTotalPages}
          </span>
          
          <button
            onClick={() => setProductPage(productPage + 1)}
            disabled={productPage === productTotalPages}
            style={{
              padding: '8px',
              background: productPage === productTotalPages ? '#222' : '#333',
              color: productPage === productTotalPages ? '#555' : '#ccc',
              border: 'none',
              borderRadius: '6px',
              cursor: productPage === productTotalPages ? 'not-allowed' : 'pointer'
            }}
          >
            <ChevronRight size={16} />
          </button>
          
          <button
            onClick={() => setProductPage(productTotalPages)}
            disabled={productPage === productTotalPages}
            style={{
              padding: '8px',
              background: productPage === productTotalPages ? '#222' : '#333',
              color: productPage === productTotalPages ? '#555' : '#ccc',
              border: 'none',
              borderRadius: '6px',
              cursor: productPage === productTotalPages ? 'not-allowed' : 'pointer'
            }}
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      )}

      {/* Dispensary Product Details Modal */}
      {showDispensaryProductModal && selectedProducts.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            padding: '32px',
            border: '1px solid #333',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#e5e5e5'
                }}>
                  Configure Dispensary Product Details
                </h3>
                <p style={{
                  margin: '8px 0 0 0',
                  color: '#888',
                  fontSize: '14px'
                }}>
                  Product {currentProductIndex + 1} of {selectedProducts.length}: {selectedProducts[currentProductIndex]?.name}
                </p>
              </div>
              <button
                onClick={() => setShowDispensaryProductModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Progress bar */}
            <div style={{
              width: '100%',
              height: '4px',
              background: '#333',
              borderRadius: '2px',
              marginBottom: '32px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${((currentProductIndex + 1) / selectedProducts.length) * 100}%`,
                height: '100%',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                transition: 'width 0.3s ease'
              }} />
            </div>

            {/* Current product info */}
            <div style={{
              background: '#2a2a2a',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: '#8b5cf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Package size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5',
                    marginBottom: '2px'
                  }}>
                    {selectedProducts[currentProductIndex]?.name}
                  </div>
                  <div style={{ fontSize: '14px', color: '#888' }}>
                    {selectedProducts[currentProductIndex]?.brand} • {selectedProducts[currentProductIndex]?.type}
                  </div>
                </div>
              </div>
            </div>

            {/* Form fields */}
            <div style={{ display: 'grid', gap: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    External Platform
                  </label>
                  <select
                    value={dispensaryProductData[selectedProducts[currentProductIndex]?.id]?.external_platform || 'Jane'}
                    onChange={(e) => handleDispensaryProductDataChange('external_platform', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  >
                    <option value="Jane">Jane</option>
                    <option value="Leafly">Leafly</option>
                    <option value="Weedmaps">Weedmaps</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    External Product ID
                  </label>
                  <input
                    type="text"
                    placeholder="Platform-specific ID"
                    value={dispensaryProductData[selectedProducts[currentProductIndex]?.id]?.external_product_id || ''}
                    onChange={(e) => handleDispensaryProductDataChange('external_product_id', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Variant/Size
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 3.5g, 1oz"
                    value={dispensaryProductData[selectedProducts[currentProductIndex]?.id]?.variant || ''}
                    onChange={(e) => handleDispensaryProductDataChange('variant', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Price ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g., 45.99"
                    value={dispensaryProductData[selectedProducts[currentProductIndex]?.id]?.price_cents || ''}
                    onChange={(e) => handleDispensaryProductDataChange('price_cents', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Platform Rating
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    placeholder="e.g., 4.5"
                    value={dispensaryProductData[selectedProducts[currentProductIndex]?.id]?.platform_rating || ''}
                    onChange={(e) => handleDispensaryProductDataChange('platform_rating', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    In Stock
                  </label>
                  <select
                    value={dispensaryProductData[selectedProducts[currentProductIndex]?.id]?.in_stock ? 'true' : 'false'}
                    onChange={(e) => handleDispensaryProductDataChange('in_stock', e.target.value === 'true')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  >
                    <option value="true">In Stock</option>
                    <option value="false">Out of Stock</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Product URL
                </label>
                <input
                  type="url"
                  placeholder="https://example.com/product"
                  value={dispensaryProductData[selectedProducts[currentProductIndex]?.id]?.url || ''}
                  onChange={(e) => handleDispensaryProductDataChange('url', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Raw JSON Data (Optional)
                </label>
                <textarea
                  placeholder="Additional platform data in JSON format"
                  value={dispensaryProductData[selectedProducts[currentProductIndex]?.id]?.raw_json || ''}
                  onChange={(e) => handleDispensaryProductDataChange('raw_json', e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            {/* Navigation buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button
                onClick={handlePrevProduct}
                disabled={currentProductIndex === 0}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: currentProductIndex === 0 ? '#222' : '#333',
                  color: currentProductIndex === 0 ? '#555' : '#ccc',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: currentProductIndex === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                ← Previous
              </button>
              
              {currentProductIndex < selectedProducts.length - 1 ? (
                <button
                  onClick={handleNextProduct}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={handleSaveDispensaryProducts}
                  disabled={saving}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '16px',
                    background: saving ? '#555' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {saving ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      Save All Products
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Product Creation Modal */}
      {showNewProductModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            padding: '32px',
            border: '1px solid #333',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#e5e5e5'
                }}>
                  Add New Product
                </h3>
                <p style={{
                  margin: '8px 0 0 0',
                  color: '#888',
                  fontSize: '14px'
                }}>
                  Product {currentNewProductIndex + 1} of {newProductsToAdd}
                </p>
              </div>
              <button
                onClick={() => setShowNewProductModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Progress bar */}
            <div style={{
              width: '100%',
              height: '4px',
              background: '#333',
              borderRadius: '2px',
              marginBottom: '32px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${((currentNewProductIndex + 1) / newProductsToAdd) * 100}%`,
                height: '100%',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                transition: 'width 0.3s ease'
              }} />
            </div>

            {/* Form fields */}
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Product Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Blue Dream"
                  value={newProductData.name}
                  onChange={(e) => handleNewProductDataChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Brand
                </label>
                <input
                  type="text"
                  placeholder="e.g., Green Garden"
                  value={newProductData.brand}
                  onChange={(e) => handleNewProductDataChange('brand', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Product Type
                </label>
                <select
                  value={newProductData.type}
                  onChange={(e) => handleNewProductDataChange('type', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                >
                  <option value="flower">Flower</option>
                  <option value="cart">Cartridge/Vape</option>
                  <option value="edible">Edible</option>
                  <option value="pre-roll">Pre-Roll</option>
                  <option value="concentrate">Concentrate</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Dynamic attributes based on type */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    THC Content
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 21%"
                    value={newProductData.attributes.THC || ''}
                    onChange={(e) => handleNewProductDataChange('attributes.THC', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    CBD Content
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 0.5%"
                    value={newProductData.attributes.CBD || ''}
                    onChange={(e) => handleNewProductDataChange('attributes.CBD', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Strain Type
                  </label>
                  <select
                    value={newProductData.attributes.strain_type || 'Hybrid'}
                    onChange={(e) => handleNewProductDataChange('attributes.strain_type', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  >
                    <option value="Sativa">Sativa</option>
                    <option value="Indica">Indica</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="CBD">CBD</option>
                  </select>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Size
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 3.5g"
                    value={newProductData.attributes.size || ''}
                    onChange={(e) => handleNewProductDataChange('attributes.size', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  </div>
              </div>

              {/* Type-specific fields */}
              {newProductData.type === 'cart' && (
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Flavor Profile
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Berry, Sweet"
                    value={newProductData.attributes.flavor || ''}
                    onChange={(e) => handleNewProductDataChange('attributes.flavor', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              {newProductData.type === 'edible' && (
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Dosage Per Unit
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 10mg per piece"
                    value={newProductData.attributes.dosage_per_unit || ''}
                    onChange={(e) => handleNewProductDataChange('attributes.dosage_per_unit', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button
                onClick={() => setShowNewProductModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: '#333',
                  color: '#ccc',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleSaveNewProduct}
                disabled={saving || !newProductData.name || !newProductData.brand}
                style={{
                  flex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px',
                  background: (saving || !newProductData.name || !newProductData.brand) ? '#555' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (saving || !newProductData.name || !newProductData.brand) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {saving ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    {currentNewProductIndex < newProductsToAdd - 1 ? 'Saving & Next...' : 'Saving & Done...'}
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    {currentNewProductIndex < newProductsToAdd - 1 ? 'Save & Next Product' : 'Save & Finish'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};